import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup';
import { Formik } from 'formik';

import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';

import {
  Button,
  Paper,
  TextField as MuiTextField,
  Typography,
  Alert as MuiAlert,
} from '@mui/material';
import { spacing } from '@mui/system';

const Alert = styled(MuiAlert)(spacing as any);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)}px;
  background: ${(props) => (props.theme.palette.mode === 'dark' ? '#141414' : '#ffffff')};
  border: 1px solid ${(props) => (props.theme.palette.mode === 'dark' ? '#1f1f1f' : '#e5e5e5')};
  border-radius: 12px;
  box-shadow: none;
  max-width: 480px;
  margin: 0 auto;

  ${(props) => props.theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(8)}px;
  }
`;

function SignUp() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return (
    <Wrapper>
      <Helmet title="Sign Up" />

      <Typography component="h1" variant="h4" align="center" gutterBottom>
        Get started
      </Typography>
      <Typography component="h2" variant="body1" align="center">
        Start creating the best possible user experience for you customers
      </Typography>

      <Formik
        initialValues={{
          name: '',
          company: '',
          email: '',
          password: '',
          confirmPassword: '',
          submit: false,
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().max(255).required('Name is required'),
          email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
          password: Yup.string()
            .min(12, 'Must be at least 12 characters')
            .max(255)
            .required('Required'),
          confirmPassword: Yup.string().when('password', {
            is: (val: any) => (val && val.length > 0 ? true : false),
            then: (schema) =>
              schema.oneOf([Yup.ref('password')], 'Both password need to be the same'),
          }),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            navigate('/auth/sign-in');
          } catch (error) {
            const message = extractApiErrorMessage(error, 'Something went wrong');

            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert sx={{ mt: 2, mb: 1 }} severity="warning">
                {errors.submit}
              </Alert>
            )}
            <TextField
              type="text"
              name="name"
              label="Name"
              value={values.name}
              error={Boolean(touched.name && errors.name)}
              fullWidth
              helperText={touched.name && errors.name}
              onBlur={handleBlur}
              onChange={handleChange}
              my={3}
            />
            <TextField
              type="text"
              name="company"
              label="Company"
              value={values.company}
              error={Boolean(touched.company && errors.company)}
              fullWidth
              helperText={touched.company && errors.company}
              onBlur={handleBlur}
              onChange={handleChange}
              my={3}
            />
            <TextField
              type="email"
              name="email"
              label="Email Address"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              my={3}
            />
            <TextField
              type="password"
              name="password"
              label="Password"
              value={values.password}
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              onBlur={handleBlur}
              onChange={handleChange}
              my={3}
            />
            <TextField
              type="password"
              name="confirmPassword"
              label="Confirm Password"
              value={values.confirmPassword}
              error={Boolean(touched.confirmPassword && errors.confirmPassword)}
              fullWidth
              helperText={touched.confirmPassword && errors.confirmPassword}
              onBlur={handleBlur}
              onChange={handleChange}
              my={3}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              Sign up
            </Button>
          </form>
        )}
      </Formik>
    </Wrapper>
  );
}

export default SignUp;
